import Resource from '@/api/resources/Resource'
import { COREENTITY_VALUES } from '@/api/resources/incident/incidentServices'
import { SampleOperation } from '@/api/operations/models/sample-operation'
import { PortionOperation } from '@/api/operations/models/portion-operation'
import { SubportionOperation } from '@/api/operations/models/subportion-operation'
import { mapKeys } from 'lodash'
import { PORTION_TYPES } from '@/api/resources/portionTypeService'
import { SUBPORTION_TYPES } from '@/api/resources/subportionTypes'
import { StoreUtils } from '@/shared/util/store-utils'

export const ARCHIVE_GROUPED_OPERATIONS = {
    SAMPLE_ARCHIVE_OPERATIONS: [ SampleOperation.ARCHIVE ],
    SAMPLE_UNARCHIVE_OPERATIONS: [ SampleOperation.UNARCHIVE ],

    PORTION_ARCHIVE_OPERATIONS: [ PortionOperation.ARCHIVE ],
    PORTION_UNARCHIVE_OPERATIONS: [
        PortionOperation.UNARCHIVE_TO_CUT,
        PortionOperation.UNARCHIVE_TO_REMAKE
    ],
    SUBPORTION_ARCHIVE_OPERATIONS: [ SubportionOperation.ARCHIVE ],
    SUBPORTION_UNARCHIVE_OPERATIONS: [
        SubportionOperation.UNARCHIVE_TO_SCAN,
        SubportionOperation.UNARCHIVE_TO_STAIN,
        SubportionOperation.UNARCHIVE_FOR_PATHOLOGY_REVIEW
    ]
}

class ArchiveService extends Resource {
    async searchEntityByLabel(laboratoryId, label, entityType) {
        switch (entityType) {
        case COREENTITY_VALUES.SAMPLE:
            return this.searchSampleByLabel(laboratoryId, label)
        case COREENTITY_VALUES.PORTION:
            return this.searchPortionByLabel(laboratoryId, label)
        case COREENTITY_VALUES.SUBPORTION:
            return this.searchSubportionByLabel(laboratoryId, label)
        default:
            throw new Error(`unexpected entity ${entityType} in searchEntityByLabel`)
        }
    }
    async searchSampleByLabel(laboratoryId, sampleLabel) {
        const config = { params: { sampleLabel } }
        return this.axios.get(`/archive/laboratories/${laboratoryId}/cases/samples/searchByLabel`, config)
            .then(response => response.data)
    }
    async searchPortionByLabel(laboratoryId, portionLabel) {
        const config = { params: { portionLabel } }
        return this.axios.get(`/archive/laboratories/${laboratoryId}/cases/samples/portions/searchByLabel`, config)
            .then(response => response.data)
    }
    async searchSubportionByLabel(laboratoryId, subportionLabel) {
        const config = { params: { subportionLabel } }
        return this.axios.get(`/archive/laboratories/${laboratoryId}/cases/samples/portions/subportions/searchByLabel`, config)
            .then(response => response.data)
    }
    archiveBatch(laboratoryId, entityType, entityBatch) {
        switch (entityType) {
        case COREENTITY_VALUES.SAMPLE:
            return this.archiveSampleBatch(laboratoryId, entityBatch)
        case COREENTITY_VALUES.PORTION:
            return this.archivePortionBatch(laboratoryId, entityBatch)
        case COREENTITY_VALUES.SUBPORTION:
            return this.archiveSubportionBatch(laboratoryId, entityBatch)
        default:
            throw new Error(`unexpected entity ${entityType.code} in archiveBatch`)
        }
    }
    archiveSampleBatch(laboratoryId, sampleBatch) {
        return this.axios.put(`/archive/laboratories/${laboratoryId}/cases/samples/archive`, sampleBatch).then(response => response.data)
    }
    archivePortionBatch(laboratoryId, portionBatch) {
        return this.axios.put(`/archive/laboratories/${laboratoryId}/cases/samples/portions/archive`, portionBatch).then(response => response.data)
    }
    archiveSubportionBatch(laboratoryId, subportionBatch) {
        return this.axios.put(`/archive/laboratories/${laboratoryId}/cases/samples/portions/subportions/archive`, subportionBatch).then(response => response.data)
    }
    unarchiveBatchPendingToBeUnarchived(laboratoryId, entityType, entityBatch) {
        switch (entityType) {
        case COREENTITY_VALUES.SAMPLE:
            return this.unarchiveSampleBatchPendingToBeUnarchived(laboratoryId, entityBatch)
        case COREENTITY_VALUES.PORTION:
            return this.unarchivePortionBatchPendingToBeUnarchived(laboratoryId, entityBatch)
        case COREENTITY_VALUES.SUBPORTION:
            return this.unarchiveSubportionBatchPendingToBeUnarchived(laboratoryId, entityBatch)
        default:
            throw new Error(`unexpected entity ${entityType.code} in archiveBatch`)
        }
    }
    unarchiveSampleBatchPendingToBeUnarchived(laboratoryId, sampleBatch) {
        return this.axios.put(`/archive/laboratories/${laboratoryId}/cases/samples/unarchivePendingToBeUnarchived`, sampleBatch).then(response => response.data)
    }
    unarchivePortionBatchPendingToBeUnarchived(laboratoryId, portionBatch) {
        return this.axios.put(`/archive/laboratories/${laboratoryId}/cases/samples/portions/unarchivePendingToBeUnarchived`, portionBatch).then(response => response.data)
    }
    unarchiveSubportionBatchPendingToBeUnarchived(laboratoryId, subportionBatch) {
        return this.axios.put(`/archive/laboratories/${laboratoryId}/cases/samples/portions/subportions/unarchivePendingToBeUnarchived`, subportionBatch).then(response => response.data)
    }
    getEntityHierarchicalIds(entity) {
        if (entity.portionId) {
            return {
                caseId: entity.caseId,
                sampleId: entity.sampleId,
                portionId: entity.portionId,
                subportionId: entity.id
            }
        } else if (entity.sampleId) {
            return {
                caseId: entity.caseId,
                sampleId: entity.sampleId,
                portionId: entity.id
            }
        }
        return {
            caseId: entity.caseId,
            sampleId: entity.id
        }
    }
    getEntityHierarchicalIdsForIncidentModal(entity) {
        const hierarchicalIds = this.getEntityHierarchicalIds(entity)
        return mapKeys(hierarchicalIds, (value, key) => {
            switch (key) {
            case 'caseId': return 'casId'
            case 'sampleId': return 'samId'
            case 'portionId': return 'porId'
            case 'subportionId': return 'supId'
            default: return key
            }
        })
    }
    updateArchivingLocation(laboratoryId, entityType, locationUpdateRequest) {
        if (entityType === COREENTITY_VALUES.SAMPLE) {
            return this.updateSampleArchivingLocation(laboratoryId, locationUpdateRequest)
        }
        if (entityType === COREENTITY_VALUES.PORTION) {
            return this.updatePortionArchivingLocation(laboratoryId, locationUpdateRequest)
        }
        if (entityType === COREENTITY_VALUES.SUBPORTION) {
            return this.updateSubportionArchivingLocation(laboratoryId, locationUpdateRequest)
        }
        throw new Error(`unexpected entity ${entityType.code} in updateArchivingLocation`)
    }
    updateSampleArchivingLocation(laboratoryId, locationUpdateRequest) {
        const caseId = locationUpdateRequest.idHierarchy.caseId
        const sampleId = locationUpdateRequest.idHierarchy.sampleId
        const data = null
        return this.axios.patch(`/archive/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/updateLocation`,
            data, { params: { archivingLocation: locationUpdateRequest.archivingLocation } })
            .then(response => response.data)
    }
    updatePortionArchivingLocation(laboratoryId, locationUpdateRequest) {
        const caseId = locationUpdateRequest.idHierarchy.caseId
        const sampleId = locationUpdateRequest.idHierarchy.sampleId
        const portionId = locationUpdateRequest.idHierarchy.portionId
        const data = null
        return this.axios.patch(`/archive/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/updateLocation`,
            data, { params: { archivingLocation: locationUpdateRequest.archivingLocation } })
            .then(response => response.data)
    }
    updateSubportionArchivingLocation(laboratoryId, locationUpdateRequest) {
        const caseId = locationUpdateRequest.idHierarchy.caseId
        const sampleId = locationUpdateRequest.idHierarchy.sampleId
        const portionId = locationUpdateRequest.idHierarchy.portionId
        const subportionId = locationUpdateRequest.idHierarchy.subportionId
        const data = null
        return this.axios.patch(
            `/archive/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/updateLocation`,
            data, { params: { archivingLocation: locationUpdateRequest.archivingLocation } })
            .then(response => response.data)
    }
    isLocationLabelRequired(entityType, entitySubtype) {
        let locationParameterCode
        if (entityType.code === COREENTITY_VALUES.SAMPLE.code) {
            locationParameterCode = 'configuration.parameter.laboratory.archive.sample-location-is-mandatory'
        } else if (entitySubtype === PORTION_TYPES.PARAFFIN_BLOCK) {
            locationParameterCode = 'configuration.parameter.laboratory.archive.paraffin-block-location-is-mandatory'
        } else if (entitySubtype === PORTION_TYPES.FROZEN_BLOCK) {
            locationParameterCode = 'configuration.parameter.laboratory.archive.frozen-block-location-is-mandatory'
        } else if (entitySubtype === SUBPORTION_TYPES.SLIDE) {
            locationParameterCode = 'configuration.parameter.laboratory.archive.slide-location-is-mandatory'
        } else {
            throw Error('Unexpected entitySubtype ' + entitySubtype)
        }
        return !!StoreUtils.selectedLaboratoryConfiguration?.frontLaboratoryParameters
            .some(laboratoryParameter =>
                laboratoryParameter.parameter.code === locationParameterCode &&
                laboratoryParameter.value === 'true')
    }
}

export default new ArchiveService()
